import React from 'react';
import { Request, Maybe } from '@/graphql/__generated__/van-credits';
import { useTranslations } from 'use-intl';
import { toTranslationKey, tForComponent } from '@/utils/translations';
import PassToCreditCommittee from '../VanCreditSimulation/PassToCreditCommittee';
import { BRAND_COLORS, GRAY_COLORS } from '@/UIlibrary/globalTheme';
import Button from '@/UIlibrary/Button/Button';
import EvaluationMatrix from '../VanCreditSimulation/EvaluationMatrix';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { Printer } from 'react-feather';
import CreditBureauReport from '../VanCreditQuestionnaire/CreditBureauReport';
import SimulationTable from '../VanCreditSimulation/SimulationTable';
import { ActivityType } from '@/gql/graphql';
import { MaritalStatus, Registry } from '@/graphql/__generated__/onboarding';
import { BusinessActivityMap, PeridicityDaysMap } from '@/utils/maps';
import { formatMoneyValue } from '@/utils/utils';
import TextSectionWithFormula from '../VanCreditSimulation/TextSectionWithFormula';
import TextArea from '@/UIlibrary/TextArea/TextArea';
interface CreditCoverProps {
	requestData: Maybe<Request> | undefined;
	refetchData: () => Promise<any>;
	registry: Registry | null | undefined;
}

const CreditCover: React.FC<CreditCoverProps> = ({ requestData, refetchData, registry }) => {
	const t = useTranslations('VanCreditRequestSimulation');

	const TextItem: React.FC<{ text: string | number; title: string; direction?: 'row' | 'col' }> = ({
		text,
		title,
		direction = 'row'
	}) => {
		return (
			<div className={`flex ${direction === 'col' ? 'flex-col' : 'flex-row'} gap-2`}>
				<Typography size="md" weight="medium">
					{t(toTranslationKey(title))}:
				</Typography>
				<Typography>{text || '-'}</Typography>
			</div>
		);
	};

	const TitleItem: React.FC<{ title: string }> = ({ title }) => {
		return (
			<div className="flex flex-row gap-2 items-start border-b border-gray-800 pb-2">
				<Typography size="lg" weight="semibold">
					{t(toTranslationKey(title)) || '-'}
				</Typography>
			</div>
		);
	};

	const Section: React.FC<{ children: React.ReactNode }> = ({ children }) => {
		return (
			<div className="px-6">
				<div className="flex flex-col gap-4 p-4 bg-white rounded-lg w-full">{children}</div>
			</div>
		);
	};

	const handlePrint = () => {
		window.print();
	};

	const formatValues = (key: string, requestData: Request): string | number => {
		if (key === 'fullName') {
			return (
				(requestData?.names || '') +
				' ' +
				(requestData?.firstLastName || '') +
				' ' +
				(requestData?.secondLastName || '')
			);
		}
		if (key === 'dependants') {
			return (
				(requestData?.personalInformation?.childDependants ?? 0) +
					(requestData?.personalInformation?.otherFamilyDependants ?? 0) || ''
			);
		}
		if (key === 'activityType') {
			const activityType = requestData?.activityOrBusinessInformation?.activityType;
			const activityDetail = requestData?.activityOrBusinessInformation?.activityDetail;
			return activityType === ActivityType.OTHER
				? activityDetail || ''
				: BusinessActivityMap[activityType as ActivityType]
					? t(toTranslationKey(BusinessActivityMap[activityType as ActivityType]))
					: activityType || '';
		}
		if (key === 'downPayment') {
			const downPayment = requestData?.creditDetails?.downPaymentAmount
				? formatMoneyValue(requestData?.creditDetails?.downPaymentAmount)
				: `${requestData?.creditDetails?.downPaymentPercentage}%`;
			return downPayment || '';
		}
		return '';
	};
	const getMaritalStatusText = (status: MaritalStatus): string => {
		const statusMap: Record<MaritalStatus, string> = {
			[MaritalStatus.MARRIED_JOINT_PROPERTY]: t('MarriedJointProperty'),
			[MaritalStatus.SINGLE]: t('Single'),
			[MaritalStatus.MARRIED_SEPARATED_PROPERTY]: t('MarriedSeparatedProperty'),
			[MaritalStatus.FREE_UNION]: t('FreeUnion'),
			[MaritalStatus.DIVORCED]: t('Divorced'),
			[MaritalStatus.WIDOWED]: t('Widowed'),
			[MaritalStatus.SEPARATED]: t('Separated')
		};
		return statusMap[status] || t('Other');
	};

	const formatRegistryValues = (
		key: string,
		registry: Registry | null | undefined
	): string | number => {
		if (!registry) return '';
		if (key === 'dateOfBirth') {
			const dateOfBirth = registry.birthInformation?.dateOfBirth;
			if (!dateOfBirth) return '';
			const birthDate = new Date(dateOfBirth);
			const today = new Date();
			let age = today.getFullYear() - birthDate.getFullYear();
			const m = today.getMonth() - birthDate.getMonth();
			if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
				age--;
			}
			return age;
		}
		if (key === 'maritalStatus') {
			return registry.personalInformation?.maritalStatus || '';
		}
		return '';
	};

	if (!requestData) {
		return null;
	}

	return (
		<div className="flex flex-col rounded-lg mt-5" style={{ backgroundColor: GRAY_COLORS.gray50 }}>
			<PassToCreditCommittee
				requestId={requestData?.requestId || ''}
				currentCreditAreaOpinion={requestData?.creditAreaOpinion || ''}
				refetchData={refetchData}
			/>
			<div className="p-5">
				<div className="flex justify-end px-6 mt-4 print:hidden">
					<Button label={t('Print')} onClick={handlePrint} color="gray" startIcon={<Printer />} />
				</div>
				<img
					src={'/logo-white.jpg'}
					alt="company-logo"
					style={{
						objectFit: 'contain',
						marginLeft: '25px',
						border: `1px solid ${BRAND_COLORS.brand500}`,
						borderRadius: '10px'
					}}
					width={'100px'}
					height={'100px'}
				/>
				<div className="flex justify-center items-center mb-4">
					<Typography type="display">{t('CreditCover')}</Typography>
				</div>

				<Section>
					<TitleItem title="GeneralData" />
					<TextItem text={formatValues('fullName', requestData)} title={'SolicitorName'} />
					<TextItem text={registry?.taxInformation?.rfc || ''} title="Rfc" />
					<TextItem text={registry?.personalInformation?.curp || ''} title="Curp" />
					<TextItem text={formatRegistryValues('dateOfBirth', registry) || ''} title="Age" />
					<TextItem
						text={
							registry?.personalInformation?.maritalStatus
								? getMaritalStatusText(registry.personalInformation.maritalStatus)
								: ''
						}
						title="MaritalStatus"
					/>
					<TextItem text={formatValues('dependants', requestData) || ''} title="Dependants" />
					<TextItem text={formatValues('activityType', requestData) || ''} title="ActivityType" />
					<TextItem
						text={requestData?.activityOrBusinessInformation?.startDate || ''}
						title="StartDate"
					/>
				</Section>
				<Section>
					<TitleItem title="CreditData" />
					<TextItem text={'Comercial'} title="CreditType" />
					<TextItem text={t('Vehicle')} title="CreditUse" />
					<TextItem
						text={formatMoneyValue(requestData?.creditDetails?.amountRequested) || ''}
						title="AmountRequestedQuestionnaire"
					/>
					<TextItem
						text={requestData?.creditDetails?.vehiclesRequested || ''}
						title="VehiclesRequested"
					/>
					<TextItem text={`${requestData?.creditDetails?.term} ${t('Months')}`} title="Term" />
					<TextItem text={formatValues('downPayment', requestData) || ''} title="DownPayment" />
				</Section>
				<div className="page-break" />
				<EvaluationMatrix
					requestData={requestData}
					requestId={requestData?.requestId || ''}
					inCreditCover={true}
				/>
				<div className="page-break" />
				<Section>
					<TitleItem title="ProposalToCommittee" />
					<TextItem text={requestData?.evaluationMatrix?.totalScore || ''} title="TotalScore" />
					<TextItem
						text={formatMoneyValue(requestData?.simulation?.configuration?.amountRequested) || ''}
						title="AmountRequested"
					/>
					<TextItem
						text={
							requestData?.simulation?.daysPerPeriod
								? PeridicityDaysMap[requestData?.simulation?.daysPerPeriod]
								: ''
						}
						title="DaysPerTerm"
					/>
					<TextItem
						text={requestData?.simulation?.configuration?.maximumNumberOfPeriods || ''}
						title="SimulationTerm"
					/>
					<TextItem
						text={requestData?.simulation?.configuration?.ordinaryInterestRate || ''}
						title="OrdinaryRate"
					/>
					<TextItem text={requestData?.simulation?.configuration?.tiieValue || ''} title="Tiie" />
					<TextItem
						text={requestData?.simulation?.configuration?.basisPoints || ''}
						title="BasisPoints"
					/>
					<TextItem
						text={formatMoneyValue(requestData?.simulation?.paymentCapacity) || ''}
						title="SimulationPaymentCapacity"
					/>
					<TextItem
						text={formatMoneyValue(requestData?.calculatedNetIncome) || ''}
						title="CalculatedNetIncome"
					/>
				</Section>
				<Section>
					<TextItem
						text={requestData?.creditAreaOpinion || ''}
						title="CreditAreaOpinion"
						direction="col"
					/>
					<TextItem text={''} title="CommitteeDeliberation" direction="col" />
					<TextItem text={''} title="CommitteeRuling" direction="col" />
					<TextItem text={''} title="ChangesRequestedByCommittee" direction="col" />
				</Section>
				<div className="page-break" />
				<Section>
					<TitleItem title="Appendix" />
					<TextItem
						text={requestData?.onboardingRegistryDeliberation || ''}
						title="Deliberation"
						direction="col"
					/>
				</Section>
				<div className="px-6">
					<CreditBureauReport
						data={requestData?.creditBureauReport}
						title="CreditBureau"
						registry={registry}
					/>
				</div>
				<Section>
					<TitleItem title="FinancialIndicators" />
					<TextSectionWithFormula
						text={requestData?.calculatedNetIncomeIndex || '-'}
						title="CalculatedNetIncomeIndex"
						numerator="CalculatedNetIncomeIndexNumerator"
						denominator="CalculatedNetIncomeIndexDenominator"
						t={tForComponent(t)}
					/>
					<TextSectionWithFormula
						text={requestData?.recoveryTimeInYears || '-'}
						title="RecuperationTime"
						numerator="RecuperationTimeNumerator"
						denominator="RecuperationTimeDenominator"
						t={tForComponent(t)}
					/>
					<TextSectionWithFormula
						text={requestData?.applicantComprehensiveOperativeEfficiency || '-'}
						title="ApplicantComprehensiveOperativeEfficiency"
						numerator="ApplicantEfficiencyNumerator"
						denominator="ApplicantEfficiencyDenominator"
						t={tForComponent(t)}
					/>
					<TextSectionWithFormula
						text={requestData?.businessComprehensiveOperativeEfficiency || '-'}
						title="BusinessComprehensiveOperativeEfficiency"
						numerator="BusinessEfficiencyNumerator"
						denominator="BusinessEfficiencyDenominator"
						t={tForComponent(t)}
					/>

					<TextSectionWithFormula
						text={requestData?.economicSolvencyI || '-'}
						title="EconomicSolvencyOne"
						numerator="EconomicSolvencyOneNumerator"
						denominator="EconomicSolvencyOneDenominator"
						t={tForComponent(t)}
					/>

					<TextSectionWithFormula
						text={requestData?.economicSolvencyIi || '-'}
						title="EconomicSolvencyTwo"
						numerator="EconomicSolvencyTwoNumerator"
						denominator="EconomicSolvencyTwoDenominator"
						t={tForComponent(t)}
					/>
				</Section>
				<div className="page-break" />
				<SimulationTable
					data={requestData?.simulation}
					loading={!requestData}
					inCreditCover={true}
				/>
				<div className="page-break" />
				<div className="px-6">
					<Typography size="lg" weight="medium">
						{t('Signatures')}
					</Typography>
					<TextArea label={''} onChange={() => {}} rows={15} disabled={true} />
				</div>
			</div>
		</div>
	);
};
export default CreditCover;
